if (!customElements.get('product-video-carousel')) {
  customElements.define(
    'product-video-carousel',
    class ProductVideoCarousel extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.video-carousel__viewport');
        this.triggers = Array.from(this.querySelectorAll('.video-carousel__trigger'));
        this.previews = Array.from(this.querySelectorAll('.video-carousel__preview'));
        this.prevButton = this.querySelector('.video-carousel__nav--prev');
        this.nextButton = this.querySelector('.video-carousel__nav--next');
        this.playerTemplate = this.querySelector('.video-carousel__player-template');

        if (!this.viewport || this.triggers.length === 0) return;

        this.openIndex = null;
        this.lastFocused = null;

        this.triggers.forEach((trigger, index) => {
          trigger.addEventListener('click', () => this.open(index));
        });

        this.onScroll = this.syncNav.bind(this);
        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.scrollByItem(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.scrollByItem(1));

        this.onKeydown = this.handleKeydown.bind(this);
        document.addEventListener('keydown', this.onKeydown);

        this.setupPreviewPlayback();
        this.syncNav();

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.syncNav());
          this.resizeObserver.observe(this.viewport);
        }
      }

      disconnectedCallback() {
        this.close();
        if (this.onScroll) this.viewport.removeEventListener('scroll', this.onScroll);
        if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.previewObserver) this.previewObserver.disconnect();
        this.stopOffsetTracking();
        if (this.player) this.player.remove();
      }

      /**
       * Previews carry no `autoplay` attribute — playback is driven by visibility so
       * five muted videos are never decoding at once.
       */
      setupPreviewPlayback() {
        const play = (video) => {
          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
        };

        if (!('IntersectionObserver' in window)) {
          this.previews.forEach(play);
          return;
        }

        this.previewObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                play(entry.target);
              } else {
                entry.target.pause();
              }
            });
          },
          { root: this.viewport, threshold: 0.25 }
        );

        this.previews.forEach((video) => this.previewObserver.observe(video));
      }

      itemStep() {
        const first = this.querySelector('.video-carousel__item');
        if (!first) return this.viewport.clientWidth;
        const gap = parseFloat(getComputedStyle(this.querySelector('.video-carousel__track')).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      scrollByItem(direction) {
        // scrollLeft runs negative from the right edge in RTL, so the step flips.
        const isRtl = getComputedStyle(this).direction === 'rtl';
        const amount = this.itemStep() * direction * (isRtl ? -1 : 1);
        this.viewport.scrollBy({ left: amount, behavior: 'smooth' });
      }

      syncNav() {
        const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
        // Sub-pixel layout means the ends rarely land on an exact integer.
        const maxScroll = scrollWidth - clientWidth;
        const hasOverflow = maxScroll > 1;

        this.dataset.overflow = hasOverflow ? 'true' : 'false';
        if (!hasOverflow) return;

        // In RTL browsers report scrollLeft as a negative offset from the right edge.
        const progress = Math.abs(scrollLeft);
        if (this.prevButton) this.prevButton.disabled = progress <= 1;
        if (this.nextButton) this.nextButton.disabled = progress >= maxScroll - 1;
      }

      ensurePlayer() {
        if (this.player) return this.player;
        if (!this.playerTemplate) return null;

        // importNode, not cloneNode: a clone of template content still belongs to the
        // template's inert document until it is inserted, and WebKit is unreliable
        // with media elements that start life there.
        this.player = document.importNode(this.playerTemplate.content.firstElementChild, true);
        this.playerVideo = this.player.querySelector('.video-player__video');
        this.playerClose = this.player.querySelector('.video-player__close');

        this.playerClose.addEventListener('click', () => this.close());
        // Playing to the end is a natural place to dismiss the player.
        this.player.addEventListener('ended', () => this.close(), true);

        // Fixed positioning has to escape the product info column, which the theme
        // animates and sticks.
        document.body.appendChild(this.player);
        return this.player;
      }

      /**
       * Desktop: keeps the player clear of the theme's bottom-fixed bars. The
       * sticky add-to-cart bar appears partway down the page, so this is
       * re-measured while the player is open, not just once on open.
       *
       * Mobile: no offset. The player floats over the bars at a fixed distance
       * (see the stylesheet). The inline value has to be removed rather than
       * zeroed in CSS, because an inline custom property outranks the stylesheet
       * — and it can linger from a desktop-width open before a rotate or resize.
       */
      syncPlayerOffset() {
        if (this.isMobile()) {
          this.player.style.removeProperty('--video-player-offset');
          return;
        }

        const bars = document.querySelectorAll('.mobile-sticky-bar, .sticky-atc-bar');
        let offset = 0;

        bars.forEach((bar) => {
          const style = getComputedStyle(bar);
          if (style.display === 'none' || style.visibility === 'hidden') return;
          const rect = bar.getBoundingClientRect();
          if (rect.height === 0) return;
          // Only bars actually parked at the bottom of the viewport matter.
          if (rect.bottom < window.innerHeight - 4) return;
          offset = Math.max(offset, rect.height);
        });

        // Only the offset is written here; the gap itself stays in the stylesheet so
        // the mobile breakpoint can change it.
        this.player.style.setProperty('--video-player-offset', `${offset}px`);
      }

      startOffsetTracking() {
        if (this.offsetTracker) return;

        let queued = false;
        this.offsetTracker = () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => {
            queued = false;
            if (this.openIndex !== null) this.syncPlayerOffset();
          });
        };

        window.addEventListener('scroll', this.offsetTracker, { passive: true });
        window.addEventListener('resize', this.offsetTracker, { passive: true });
      }

      stopOffsetTracking() {
        if (!this.offsetTracker) return;
        window.removeEventListener('scroll', this.offsetTracker);
        window.removeEventListener('resize', this.offsetTracker);
        this.offsetTracker = null;
      }

      open(index) {
        const trigger = this.triggers[index];
        const src = trigger && trigger.dataset.videoSrc;
        const player = this.ensurePlayer();
        if (!src || !player) return;

        if (this.openIndex === index) {
          this.playerVideo.focus({ preventScroll: true });
          return;
        }

        this.lastFocused = trigger;
        this.openIndex = index;

        const video = this.playerVideo;

        // Reveal before touching the media: iOS will not reliably start playback on
        // an element that is display:none or fully transparent.
        player.hidden = false;
        if (this.isMobile()) this.lockScroll();
        // Measured after the lock so it reflects the page as it will actually sit.
        this.syncPlayerOffset();
        this.startOffsetTracking();

        // Commit the closed-state styles, then flip to open in the same tick. The
        // player is visible by the time play() runs, and the transition still
        // animates because the starting state was already laid out.
        void player.offsetHeight;
        player.classList.add('is-open');

        // One persistent element: new src, load(), play() — all synchronous inside
        // the tap handler so iOS still treats it as user-initiated. Without the
        // explicit load(), iOS reports "playing" with nothing fetched, which is the
        // black frame at 00:00.
        video.pause();
        video.muted = false;
        video.volume = 1;
        video.poster = trigger.dataset.videoPoster || '';
        video.src = src;
        video.load();

        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          // If audible playback is refused, fall back to muted rather than a dead frame.
          attempt.catch(() => {
            if (this.openIndex !== index) return;
            video.muted = true;
            const retry = video.play();
            if (retry && typeof retry.catch === 'function') retry.catch(() => {});
          });
        }

        // preventScroll matters while the body is pinned for the scroll lock.
        this.playerClose.focus({ preventScroll: true });
      }

      isMobile() {
        return window.matchMedia('(max-width: 767.98px)').matches;
      }

      /**
       * Mobile only. `overflow: hidden` on the body is not enough on iOS Safari, so
       * the body is pinned and the scroll offset restored on close.
       */
      lockScroll() {
        if (this.scrollLocked) return;
        this.lockedScrollY = window.scrollY;
        document.body.style.top = `-${this.lockedScrollY}px`;
        document.body.classList.add('video-player-lock');
        this.scrollLocked = true;
      }

      unlockScroll() {
        if (!this.scrollLocked) return;
        document.body.classList.remove('video-player-lock');
        document.body.style.top = '';
        window.scrollTo(0, this.lockedScrollY || 0);
        this.scrollLocked = false;
      }

      close() {
        if (!this.player || this.openIndex === null) return;

        const trigger = this.lastFocused;
        this.playerVideo.pause();
        this.player.classList.remove('is-open');
        this.openIndex = null;
        this.stopOffsetTracking();
        this.unlockScroll();

        const finish = () => {
          if (this.openIndex !== null) return;
          this.player.hidden = true;
          // Dropping src and reloading is what actually stops the download.
          this.playerVideo.removeAttribute('src');
          this.playerVideo.removeAttribute('poster');
          this.playerVideo.load();
        };

        this.player.addEventListener('transitionend', finish, { once: true });
        // transitionend never fires under reduced motion.
        setTimeout(finish, 300);

        // The scroll position was just restored by unlockScroll; do not fight it.
        if (trigger && document.body.contains(trigger)) trigger.focus({ preventScroll: true });
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && this.openIndex !== null) {
          event.preventDefault();
          this.close();
        }
      }
    }
  );
}
